import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonEntityService } from '../../services/common-entity.service';
import { ENTITY_FIELD_CONFIGURATIONS, DupDialogEntityConfig } from './constants';
import { MessageService, MenuItem } from 'primeng/api';
import { AgGridToolsService } from '../../ag-grid-table/services/ag-grid-tools.service';
import { OptionsParams, UtilitiesService } from '../../services/utilities.service';
import { SpinnerService } from 'src/app/common/services/spinner.service';
import { OfficeTimeFormPipe } from '../../pipes/officeTimeForm.pipe';
interface CustomValue {
  [key: string]: any;
}

interface DuplicationPayload {
  parentId: string;
  fieldsToDuplicate: string[];
  customValues: CustomValue[];
}

// Add new interface for bulk records
interface BulkRecords {
  [key: string]: any;
}

// Update the interface for duplicated records
interface DuplicatedRecord {
  sourceId: string;
  newId: string;
  success: boolean;
  subCreations?: any;
  error?: string;
}

@Component({
  selector: 'app-dup-dialog',
  templateUrl: './dup-dialog.component.html',
  styleUrls: ['./dup-dialog.component.sass'],
  providers: [MessageService],
})
export class DupDialogComponent implements OnInit {

  @Input() entity: string;
  @Input() viewRoute: string;
  @Input() record: any;
  @Output() duplicationSuccess = new EventEmitter<any>();

  private savedSelections: { [key: string]: boolean } = {};

  constructor(
    private commonEntityService: CommonEntityService,
    private messageService: MessageService,
    private agGridToolsService: AgGridToolsService,
    private utilitiesService: UtilitiesService,
    private spinnerService: SpinnerService,
    private officeTimeFormPipe: OfficeTimeFormPipe
  ) {}

  public _display: boolean = false;
  public payload: any = {};
  public tableData: any[] = [];
  public allSelected: boolean = false;
  public isStoreEntity: boolean = false;
  private storeEnv: 'dev' | 'test' | 'qa' | 'prod' = 'dev'
  public isBulk: boolean = false;
  public options: any[] = [];

  // Add new properties for bulk handling
  public records: BulkRecords = {};
  public currentRecordIndex: number = 0;
  public totalRecords: number = 0;
  
  // Add property for accordion states
  public expandedPanels: { [key: string]: boolean } = {};
  public allPanelsExpanded: boolean = true;

  // Add properties for results
  public duplicatedRecords: DuplicatedRecord[] = [];
  public duplicationProgress: number = 0;
  public isDuplicating: boolean = false;

  public apiOptions: any = {};

  // Getter to provide fallback route (viewRoute or entity)
  get route(): string {
    return this.viewRoute || this.entity || '';
  }

  @Input()
  set display(value: boolean) {
    if (value) {
      this.restoreSelections();
    }
    this._display = value;
  }
  get display(): boolean {
    return this._display;
  }

  async ngOnInit(): Promise<void> {
    if (['price-points', 'store-listings-v2'].includes(this.entity)) {
      const selectedEnvironment = this.agGridToolsService.getSelectedEnvironment();
      this.storeEnv = selectedEnvironment.value;
      this.isStoreEntity = true;
    }
  }

  async show(payload: any): Promise<void> {
    this.records = {};
    this.expandedPanels = {};
    this.duplicatedRecords = [];
    this.payload = this.normalizePayload(payload);

    if (this.payload.isBulk) {
      this.isBulk = true;
      this.payload._id.forEach((id: string) => {
        this.expandedPanels[id] = true;
      });
    } else {
      this.isBulk = false;
    }

    const loaded = await this.getRecordData();
    if (!loaded) {
      return;
    }
    await this.buildDataForDisplay();

    this.display = true;
  }

  hide(): void {
    this.payload = {};
    this.records = {};
    this.expandedPanels = {};
    this.display = false;
  }

  async getRecordData(): Promise<boolean> {
    if (this.isBulk) {
      const requestedIds: string[] = [...(this.payload._id || [])];
      const results = await this.loadBulkRecords(requestedIds);

      this.records = {};
      results.forEach((record) => {
        if (!record || record._id == null) {
          return;
        }
        this.records[this.toIdString(record._id)] = record;
      });

      const loadedIds = requestedIds.filter((id: string) => !!this.records[id]);
      const failedCount = requestedIds.length - loadedIds.length;
      if (failedCount > 0) {
        this.messageService.add({
          sticky: true,
          severity: loadedIds.length === 0 ? 'error' : 'warn',
          summary: 'Duplication Error',
          detail: loadedIds.length === 0
            ? `Could not load any of the ${requestedIds.length} selected records.`
            : `${failedCount} of ${requestedIds.length} selected records could not be loaded and were skipped.`,
        });
      }

      this.payload._id = loadedIds;
      this.payload._id.forEach((id: string) => {
        this.expandedPanels[id] = true;
      });
      this.record = this.records[this.payload._id[0]] || results.find((record) => record && record._id != null) || null;
      this.totalRecords = this.payload._id.length;
      this.currentRecordIndex = 0;
      return loadedIds.length > 0;
    }

    this.record = await this.commonEntityService.findOneWithQuery(this.entity, { query: { _id: this.payload._id } });
    if (!this.record || this.record._id == null) {
      this.messageService.add({
        sticky: true,
        severity: 'error',
        summary: 'Duplication Error',
        detail: 'Could not load the selected record.',
      });
      return false;
    }
    return true;
  }

  private async loadBulkRecords(requestedIds: string[]): Promise<any[]> {
    if (requestedIds.length === 0) {
      return [];
    }

    let records: any[] = [];
    try {
      const result = await this.commonEntityService.findAllWithQuery(this.entity, {
        query: { _id: { $in: requestedIds } },
      });
      records = Array.isArray(result) ? result : [];
    } catch {
      records = [];
    }

    const loadedIds = new Set(
      records.map((record) => this.toIdString(record?._id)).filter(Boolean)
    );
    const missingIds = requestedIds.filter((id) => !loadedIds.has(id));
    if (missingIds.length === 0) {
      return records;
    }

    const recovered = await Promise.all(
      missingIds.map((_id: string) =>
        this.commonEntityService.findOneWithQuery(this.entity, { query: { _id } }).catch(() => null)
      )
    );
    recovered.forEach((record) => {
      if (record && record._id != null) {
        records.push(record);
      }
    });
    return records;
  }

  async buildDataForDisplay() {
    const allowedFieldsForDup = ENTITY_FIELD_CONFIGURATIONS[this.entity] || [];

    if (this.isBulk) {
      await Promise.all(Object.keys(this.records).map(async (recordId) => {
        const currentRecord = this.records[recordId];
        if (!currentRecord) {
          return;
        }
        this.records[recordId].tableData = await this.buildTableDataForRecord(currentRecord, allowedFieldsForDup);
      }));
    } else if (this.record) {
      this.tableData = await this.buildTableDataForRecord(this.record, allowedFieldsForDup);
    } else {
      this.tableData = [];
    }
  }

  private normalizePayload(payload: any): any {
    if (!payload) {
      return {};
    }
    if (payload.isBulk) {
      const ids = Array.isArray(payload._id) ? payload._id : payload._id ? [payload._id] : [];
      return {
        ...payload,
        _id: ids.map((id: any) => this.toIdString(id)).filter(Boolean),
      };
    }
    return {
      ...payload,
      _id: this.toIdString(payload._id),
    };
  }

  private toIdString(id: any): string {
    if (id == null) {
      return '';
    }
    if (typeof id === 'string') {
      return id;
    }
    if (typeof id === 'object') {
      if (id.$oid) {
        return String(id.$oid);
      }
      if (typeof id.toHexString === 'function') {
        return id.toHexString();
      }
      if (id._id && id._id !== id) {
        return this.toIdString(id._id);
      }
      const asString = typeof id.toString === 'function' ? id.toString() : '';
      if (asString && asString !== '[object Object]') {
        return asString;
      }
    }
    return String(id);
  }

  // Helper method to build table data for a single record
  private buildTableDataForRecord(record: any, allowedFields: any[]) {
    return Promise.all(allowedFields.map(async field => {
      let fieldValue = record[field.key];

      if (this.isStoreEntity && field.isStoreValue) {
        fieldValue = fieldValue ? fieldValue[this.storeEnv] : undefined;
      }

      let displayValue;
      let showDateInputs = false;
      let showNameInput = false;
      let showTTLInput = false;
      let showEnvInput = false;
      let showProductId = false;
      let showLinkRef = false;
      switch (field.type) {
        case 'text':
          displayValue = Array.isArray(fieldValue) ? fieldValue.join(', ') : fieldValue;
          break;
        case 'richText':
          displayValue = fieldValue || '';
          break;
        case 'multiselect':
          displayValue = fieldValue ? fieldValue.join(', ') : '';
          break;
        case 'ref':
          displayValue = fieldValue ? `${fieldValue.name} (${fieldValue.id || fieldValue._id})` : '';
          break;
        case 'date':
          displayValue = fieldValue ? new Date(fieldValue).toISOString().split('T')[0] : '';
          break;
        case 'dateInput':
          if(field.newValue) {
            displayValue = new Date(new Date().setHours(0, 0, 0, 0));
          } else {
            displayValue = fieldValue ? this.officeTimeFormPipe.transform(fieldValue) : '';
          }
          break;
        case 'multiselectDups':
          displayValue = fieldValue?.map((item: any) => ({
            id: item.id,
            _id: item._id,
            name: item.name,
            start: item.start,
            end: item.end,
            selected: true,
            ttl: typeof item.ttl === 'number' ? item.ttl : (field.key === 'inboxMessages_ref' ? 504 : 1),
            env: field.showEnvInput && field.envDefaultValues ? field.envDefaultValues : item.env ? item.env : null,
            productId: item.productId || null,
            link_ref: item.link_ref || null
          }));
          let params = new OptionsParams();
          params.entity = field.controller;
          await this.utilitiesService.getOptions(this.options, field.key, params);
          showDateInputs = field.showDateInputs || false;
          showNameInput = field.showNameInput || false;
          showTTLInput = field.showTTLInput || false;
          showEnvInput = field.showEnvInput || false;
          showProductId = field.showProductId || false;
          showLinkRef = field.showLinkRef || false;

          break;
        case 'multiselectEnv':
          if(field.defaultValue) {
            displayValue = field.defaultValue;
          } else {
            displayValue = fieldValue || [];
          }
          break;
        case 'singleDup':
          displayValue = fieldValue ? {
            id: fieldValue.id,
            _id: fieldValue._id,
            name: fieldValue.name,
            start: fieldValue.start,
            end: fieldValue.end,
            selected: true,
            ttl: typeof fieldValue.ttl === 'number' ? fieldValue.ttl : (field.key === 'announcementInbox_ref' ? 504 : 1),
            env: fieldValue.env,
            productId: fieldValue.productId || null,
            link_ref: fieldValue.link_ref || null
          } : '';
          showDateInputs = field.showDateInputs || false;
          showNameInput = field.showNameInput || false;
          showTTLInput = field.showTTLInput || false;
          showEnvInput = field.showEnvInput || false;
          showProductId = field.showProductId || false;
          showLinkRef = field.showLinkRef || false;

          break;
        case 'boolean':
          displayValue = fieldValue ? 'Yes' : 'No';
          break;
        case 'simpleDropdown':
          displayValue = fieldValue || null;
          break;
        case 'booleanInput':
          displayValue = field.defaultValue !== undefined ? field.defaultValue : fieldValue;
          break;
        case 'reward_track_rewards':
          displayValue = this.getTrackKeys(record.type);
          break;
        default:
          displayValue = fieldValue;
      }

      if(showLinkRef) {
        const options = new OptionsParams();
        options.entity = 'link-destination';
        options.select = '_id id linkText linkDestination';
        this.apiOptions[field.key] = await this.utilitiesService.getOptions(
          this.apiOptions, field.key, options);
      }

      return {
        key: field.key,
        controller: field.controller || null,
        label: field.label,
        type: field.type,
        value: displayValue,
        duplicate: field.required || field.duplicate ? true : false,
        required: field.required ? true : false,
        isInput: field.isInput ? true : false,
        options: field.options || null,
        onLabel: field.onLabel || null,
        offLabel: field.offLabel || null,
        duplicateMessage: field.duplicateMessage || null,
        isRewardTrack: field.isRewardTrack || false,
        optionLabel: field.optionLabel || null,
        optionValue: field.optionValue || null,
        ...(showDateInputs && { showDateInputs }),
        ...(showNameInput && { showNameInput }),
        ...(showTTLInput && { showTTLInput }),
        ...(showEnvInput && { showEnvInput }),
        ...(showProductId && { showProductId }),
        ...(showLinkRef && { showLinkRef }),
      };
    }));
  }

  // Update the selectionTableData getter
  public get selectionTableData(): any[] {
    if (this.isBulk && 
        Object.keys(this.records).length > 0 && 
        this.payload?._id?.length > 0) {  // Add validation for payload._id
      // For bulk, use the first record's table data as template
      const firstRecordId = this.payload._id[0];
      return this.records[firstRecordId]?.tableData || [];
    }
    return this.tableData;
  }

  // Update the toggleAllSelection method
  toggleAllSelection() {
    this.allSelected = !this.allSelected;
    if (this.isBulk) {
      Object.keys(this.records).forEach(recordId => {
        this.records[recordId].tableData?.forEach((row: any) => {
          row.duplicate = this.allSelected || row.required;
          this.savedSelections[row.label] = row.duplicate;
        });
      });
    } else {
      this.tableData?.forEach(row => {
        row.duplicate = this.allSelected || row.required;
        this.savedSelections[row.label] = row.duplicate;
      });
    }
  }

  // Update onItemSelected method
  onItemSelected(rowData: any) {
    this.savedSelections[rowData.label] = rowData.duplicate;
    
    if (this.isBulk) {
      Object.keys(this.records).forEach(recordId => {
        const matchingRow = this.records[recordId].tableData?.find((row: any) => row.label === rowData.label);
        if (matchingRow) {
          matchingRow.duplicate = rowData.duplicate;
        }
      });
    }
    
    this.updateAllSelected();
  }

  // Update updateAllSelected method
  updateAllSelected() {
    if (this.isBulk) {
      const firstRecordId = this.payload._id[0];
      this.allSelected = this.records[firstRecordId]?.tableData?.every((item: any) => item.duplicate);
    } else {
      this.allSelected = this.tableData?.every(item => item.duplicate);
    }
  }

  clearSelections() {
    this.allSelected = false;
    this.savedSelections = {};
    this.tableData?.forEach(row => {
      row.duplicate = row.required || false;
    });
  }

  async onDuplicate() {
    this.spinnerService.loadSpecificSpinner('dup-dialog-spinner');
    this.isDuplicating = true;
    this.duplicationProgress = 0;

    if (!this.isBulk) {
      try {
        const selectedFields = this.tableData
          .filter(item => item.duplicate)
          .map(item => ({
            key: item.key,
            value: item.value,
            type: item.type,
            isInput: item.isInput
          }));

        if (selectedFields.length === 0) {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: 'Duplication Error',
            detail: 'No fields selected for duplication. Please select fields to duplicate and try again.'
          });
          // Reset before bailing out: the cleanup at the end of this method is
          // skipped by an early return, which would leave the dialog stuck
          // showing the progress spinner with every action button hidden.
          this.isDuplicating = false;
          this.spinnerService.endSpecificSpinner('dup-dialog-spinner');
          return;
        }

        const payload: DuplicationPayload = {
          parentId: this.record._id,
          fieldsToDuplicate: selectedFields.map(field => field.key),
          customValues: []
        };

        if (this.isStoreEntity || DupDialogEntityConfig.hasCustomDuplicateValues(this.entity)) {
          payload.customValues = selectedFields.filter(field => field.isInput).map(field => ({ [field.key]: field.value }));
          payload.customValues = this.cleanUnselectedCustomValues(payload.customValues);
          if(this.entity === 'rewards-track'){
            const rewardTrackRewardsField = selectedFields.find(field => field.key === 'reward_track_rewards');
            if (rewardTrackRewardsField && rewardTrackRewardsField.value) {
              payload.customValues.push({ reward_track_rewards: rewardTrackRewardsField.value });
            }
          }
        }

        const result = await this.commonEntityService.duplicate(this.entity, payload);
        this.duplicatedRecords.push({ 
          sourceId: this.record.id,
          newId: result.id,
          success: true,
          subCreations: result.subCreations
        });
        this.duplicationProgress = 100;
        this.duplicationSuccess.emit(result);

        setTimeout(() => {
          if (result?.id) {
            window.open(`/${this.route}/${result.id}`, '_blank');
          } else {
            console.error('Duplicate result missing id field:', result);
            this.messageService.add({
              severity: 'warn',
              summary: 'Navigation Warning',
              detail: 'Record duplicated successfully, but could not navigate to the new record. Please find it manually.'
            });
          }
        }, 500);
      } catch (error: any) {
        console.log(error);

        // Sticky: the API now reports which phase failed and whether the partial
        // records were rolled back. That can be the difference between a safe
        // retry and creating a second set of orphans, so it must not auto-dismiss
        // before the operator has read it.
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: 'Duplication Error',
          detail: error?.error?.message || 'Failed to duplicate record'
        });

        this.duplicatedRecords.push({ 
          sourceId: this.record.id,
          newId: '',
          success: false, 
          error: JSON.stringify(error)
        });
        this.duplicationProgress = 100;
      }
    } else {
      const totalRecords = this.payload._id.length;
      let processedCount = 0;

      for (const currentId of this.payload._id) {
        try {
          const currentRecord = this.records[currentId];
          if (!currentRecord?.tableData) {
            this.duplicatedRecords.push({
              sourceId: currentId,
              newId: '',
              success: false,
              error: 'Record data was not loaded'
            });
          } else {
            const selectedFields = currentRecord.tableData
              .filter((item: any) => item.duplicate)
              .map((item: any) => item.key);

            const payload: DuplicationPayload = {
              parentId: currentId,
              fieldsToDuplicate: selectedFields,
              customValues: []
            };

            if (this.isStoreEntity || DupDialogEntityConfig.hasCustomDuplicateValues(this.entity)) {
              payload.customValues = currentRecord.tableData
                .filter((item: any) => item.isInput && item.duplicate)
                .map((field: any) => ({ [field.key]: field.value }));
              payload.customValues = this.cleanUnselectedCustomValues(payload.customValues);
              if (this.entity === 'rewards-track') {
                const rewardTrackRewardsField = currentRecord.tableData.find((field: any) => field.key === 'reward_track_rewards' && field.duplicate);
                if (rewardTrackRewardsField && rewardTrackRewardsField.value) {
                  payload.customValues.push({ reward_track_rewards: rewardTrackRewardsField.value });
                }
              }
            }

            const result = await this.commonEntityService.duplicate(this.entity, payload);
            this.duplicatedRecords.push({
              sourceId: currentRecord.id,
              newId: result.id,
              success: true,
              subCreations: result.subCreations
            });
            this.duplicationSuccess.emit(result);
          }
        } catch (error: any) {
          this.duplicatedRecords.push({ 
            sourceId: this.records[currentId]?.id || currentId,
            newId: '',
            success: false, 
            error: error?.error?.message || 'Unknown error'
          });
        }
        
        processedCount++;
        this.duplicationProgress = Math.ceil((processedCount / totalRecords) * 100);
      }
    }

    this.isDuplicating = false;
    this.clearSelections();
    this.savedSelections = {};
    this.spinnerService.endSpecificSpinner('dup-dialog-spinner');
    await this.buildDataForDisplay();
  }

  onCancel() {
    this.clearSelections();
    this.display = false;
  }

  // Add methods to navigate between records in bulk mode
  nextRecord() {
    if (this.currentRecordIndex < this.totalRecords - 1) {
      this.currentRecordIndex++;
      this.buildDataForDisplay();
    }
  }

  previousRecord() {
    if (this.currentRecordIndex > 0) {
      this.currentRecordIndex--;
      this.buildDataForDisplay();
    }
  }

  private restoreSelections() {
    if (this.allSelected) {
      // If all was selected, select all rows
      this.tableData?.forEach(row => row.duplicate = true);
    } else {
      // Restore individual selections
      this.tableData?.forEach(row => {
        row.duplicate = this.savedSelections[row.label] || row.required || row.duplicate || false;
      });
    }
  }

  private saveSelections() {
    // Save current selections
    this.savedSelections = {};
    this.tableData?.forEach(row => {
      this.savedSelections[row.label] = row.duplicate;
    });
  }

  // When dialog visibility changes
  ngOnChanges(changes: SimpleChanges) {
    if (changes['display'] && !changes['display'].firstChange) {
      if (this.display) {
        this.restoreSelections();
      } else {
        this.saveSelections();
      }
    }
  }

  // Add method to toggle accordion panels
  togglePanel(recordId: string) {
    this.expandedPanels[recordId] = !this.expandedPanels[recordId];
  }

  // Add method to toggle all panels
  toggleAllPanels() {
    this.allPanelsExpanded = !this.allPanelsExpanded;
    Object.keys(this.expandedPanels).forEach(recordId => {
      this.expandedPanels[recordId] = this.allPanelsExpanded;
    });
  }

  // Add method to handle value changes from row component
  onRowValueChange(rowData: any, newValue: any) {
    const value = newValue && typeof newValue === 'object' && 'value' in newValue
      ? newValue.value
      : newValue;

    if (rowData.type === 'nameInput') {
      rowData.value = typeof value === 'object'
        ? (value?.toString ? value.toString() : JSON.stringify(value))
        : String(value ?? '');
    } else {
      rowData.value = value;
    }
  }

  private cleanUnselectedCustomValues(customValues: any[]): any[] {
    return customValues.map(customValue => {
      const [key] = Object.keys(customValue);
      const value = customValue[key];
      
      // If the value is an array and its items have a 'selected' property
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'selected' in value[0]) {
        return {
          [key]: value.filter(item => item.selected)
        };
      }
      
      return customValue;
    }).filter(customValue => {
      const [key] = Object.keys(customValue);
      const value = customValue[key];
      return !Array.isArray(value) || value.length > 0;
    });
  }

  private getTrackKeys(type: number) {
    let trackKeys: string[] = [];
    let trackKeyLabels: string[] = [];
    let multiKeyStages: boolean = false;
    let milestoneLabel: string = '';
    switch (type) {
        case 1: // Daily Login Rewards - 7 days
            trackKeys = [
                'dailyRewardDay1', 'dailyRewardDay2', 'dailyRewardDay3',
                'dailyRewardDay4', 'dailyRewardDay5', 'dailyRewardDay6', 'dailyRewardDay7'
            ];
            multiKeyStages = true;
            trackKeyLabels = ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5', 'Day 6', 'Day 7'];
            break;
        case 2: // Daily Login Rewards - Day 30
            trackKeys = ['dailyRewardDay30'];
            trackKeyLabels = ['Day 30'];
            multiKeyStages = false;
            break;
        case 3: // Season Pass - Free track
            trackKeys = ['free'];
            trackKeyLabels = ['Free'];
            milestoneLabel = 'Target';
            multiKeyStages = false;
            if (!this.record.free) {
                this.record.free = [];
            }
            break;
        case 4: // Season Pass - Paid track
            trackKeys = ['paid'];
            trackKeyLabels = ['Paid'];
            multiKeyStages = false;
            break;
        case 5: // Friends Invited
            trackKeys = ['friends'];
            trackKeyLabels = ['Friends Invited'];
            milestoneLabel = 'Friends Invited';
            multiKeyStages = false;
            break;
        case 6: // Endless Offer
            trackKeys = ['EOrewards'];
            trackKeyLabels = ['Endless Offer'];
            // touchIsLoop = true;
            multiKeyStages = false;
            break;
        case 7: // Co-Op
            trackKeys = ['coop'];
            trackKeyLabels = ['Co-Op'];
            milestoneLabel = 'Reward';
            multiKeyStages = false;
            break;
        case 8: // Mini Games
            trackKeys = ['miniGamesRewards'];
            trackKeyLabels = ['Mini Games'];
            multiKeyStages = false;
            // touchIsLoop = true;
            // showIsLoop = true;
            break;
        case 9: // Seasonal Prizes
            trackKeys = ['seasonalPrizes'];
            trackKeyLabels = ['Seasonal Prizes'];
            multiKeyStages = false;
            break;
        case 10: // Daily Activities
            trackKeys = ['dailyActivitiesRewards'];
            trackKeyLabels = ['Daily Activities'];
            multiKeyStages = false;
            break;
        case 11: // Perk Daily Reward
            trackKeys = ['perkDailyReward'];
            trackKeyLabels = ['Perk Daily'];
            multiKeyStages = false;
            break;
        default:
            trackKeys = ['error'];
            trackKeyLabels = ['Error: Could not match track type'];
    }
    return { trackKeys, trackKeyLabels, multiKeyStages, milestoneLabel };
  }
}
